/* eslint-disable no-param-reassign */
import * as marietta from '../..';
import { Point } from '../../core';
import { BUILDING_TYPE_MANSION } from '../Constants';
import { SVGSprite } from '../flash/display';
import { LineSegmentSnap, LineSnap } from './parts';

export type SnapElemnt = marietta.webcad.manager.parts.LineSnap|marietta.webcad.manager.parts.LineSegmentSnap|marietta.webcad.manager.parts.PointSnap;

/**
 * スナップを扱うクラス
 */
export default class SnapManager {
  // スナップ判定半径。拡大率で補正するので画面上の距離は一定になる
  public static readonly SNAP_RADIUS = 50;

  // 補助線どうしの交点の判定半径。図面上に実在しない点なので実要素より狭くする
  public static readonly CROSS_POINT_SNAP_RADIUS = 20;

  // 入力中の水平・垂直ガイドの優先度。入力が進んだガイドは PAST に落とす
  public static readonly CURRENT_GUIDE_PRIORITY = 2;

  public static readonly PAST_GUIDE_PRIORITY = 3;

  pathH: SVGSprite; // 横スナップガイドライン

  pathV: SVGSprite; // 縦スナップガイドライン

  pathC: SVGSprite; // ポイントスナップガイドライン

  pathL: SVGSprite; // 線分スナップのガイドライン

  constructor() {
    this.pathH = new SVGSprite((document.querySelector('#lib_snapRefLineh svg > g').cloneNode(true) as SVGElement));
    this.pathV = new SVGSprite((document.querySelector('#lib_snapRefLinev svg > g').cloneNode(true) as SVGElement));
    this.pathC = new SVGSprite((document.querySelector('#lib_snapRefLinec svg > g').cloneNode(true) as SVGElement));
    this.pathL = new SVGSprite((document.querySelector('#lib_snapRefLine svg > g').cloneNode(true) as SVGElement));
  }

  /**
   * 対象レイヤーにガイドライン要素を作成する機能
   * @param tarLayer
   */
  createSnapGuide(tarLayer: marietta.webcad.layer.FloorShitaeLayer) {
    const _snapGuideObj: SVGGElement = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    _snapGuideObj.setAttribute('id', 'controller_snap_guide');

    tarLayer.viewObj.appendChild(_snapGuideObj);
    const snapGuideSpt = new SVGSprite(_snapGuideObj);

    snapGuideSpt.addChild(this.pathH);
    snapGuideSpt.addChild(this.pathV);
    snapGuideSpt.addChild(this.pathC);
    snapGuideSpt.addChild(this.pathL);
  }

  /**
   * 縦のガイドラインを表示
   * @param snap
   */
  showVerticalSnapLine(snap: {x:number;y:number;distance:number}) {
    this.pathV.show();
    const transformMatrix = `matrix(1,0,0,1,${snap.x},${0})`;
    this.pathV.element.setAttribute('transform', transformMatrix);
  }

  /**
   * 水平方向のガイドラインを表示
   * @param snap
   */
  showHorizontalSnapLine(snap: {x:number;y:number;distance:number}) {
    this.pathH.show();
    const transformMatrix = `matrix(1,0,0,1,${0},${snap.y})`;
    this.pathH.element.setAttribute('transform', transformMatrix);
  }

  /**
   * ポイントスナップガイド線を表示
   * @param snap
   */
  showCenterSnapLine(snap: {x:number;y:number;distance:number}) {
    this.pathC.show();
    const transformMatrix = `matrix(1,0,0,1,${snap.x},${snap.y})`;
    this.pathC.element.setAttribute('transform', transformMatrix);
  }

  /**
   * 線分ガイドラインを表示
   * @param snap
   */
  showSnapLineSegment(snap: LineSegmentSnap) {
    this.pathL.show();
    this.pathL.element.getElementsByTagName('line')[0].setAttribute('x1', String(snap.x1));
    this.pathL.element.getElementsByTagName('line')[0].setAttribute('y1', String(snap.y1));
    this.pathL.element.getElementsByTagName('line')[0].setAttribute('x2', String(snap.x2));
    this.pathL.element.getElementsByTagName('line')[0].setAttribute('y2', String(snap.y2));
  }

  /**
   * ポイントスナップガイド線を非表示
   */
  hideCenterSnapLine() {
    this.pathC.hide();
  }

  /**
   * 線分ガイドラインを非表示にする
   */
  hideSnapLineSegment() {
    this.pathL.hide();
  }

  /**
   * 縦のガイドラインを非表示
   */
  hideVerticalSnapLine() {
    this.pathV.hide();
  }

  /**
   * 水平方向のガイドラインを非表示
   */
  hideHorizontalSnapLine() {
    this.pathH.hide();
  }

  hideAll() {
    this.hideCenterSnapLine();
    this.hideSnapLineSegment();
    this.hideVerticalSnapLine();
    this.hideHorizontalSnapLine();
  }

  snapElements: SnapElemnt[] = [];

  /**
   * 壁スナップ要素を更新する
   */
  updateSnapElements() {
    // if (marietta.webcad.WebCADGlobal.webcad2Dmanager.buildingType !== BUILDING_TYPE_MANSION) return;
    const lineSnap_ = () => {
      const lines: marietta.webcad.manager.parts.LineSnap[] = [];

      const _add = (snapElements: SnapElemnt[], a: number, b: number, c: number, radius: number, priority: number, related: string, addCrossPoint: boolean = false) => {
        const alreadyPresent = lines.some((lineSnap: LineSnap) => a === lineSnap.a && b === lineSnap.b && c === lineSnap.c && radius === lineSnap.radius);
        if (alreadyPresent) return;
        if (addCrossPoint) {
          lines.forEach((s1) => {
            const inter = marietta.webcad.WebCADGlobal.snapManager.twoLinesIntersection(s1.a, s1.b, s1.c, a, b, c);
            if (inter) this.addPointSnap(snapElements, inter.x, inter.y, SnapManager.CROSS_POINT_SNAP_RADIUS, 40, null);
          });
        }
        const line = new marietta.webcad.manager.parts.LineSnap(a, b, c, radius, priority, related);
        snapElements.push(line);
        lines.push(line);
      };

      const _description = () => console.log(`snapelements : ${lines.length}`);
      return {
        add: _add,
        description: _description,
      };
    };

    const lineSnap = lineSnap_();

    const tarFloor = marietta.webcad.WebCADGlobal.layerGroup.getFloorLayer(marietta.webcad.WebCADGlobal.webcad2Dmanager.currentFloor);
    if (!tarFloor) return;
    let rooms: marietta.webcad.item.MonicaObject[] = tarFloor.layer_room.getAllChildren() as marietta.webcad.item.MonicaObject[];
    const columns: marietta.webcad.item.MonicaObject[] = tarFloor.layer_column.getAllChildren();
    if (columns.length > 0) {
      rooms = rooms.concat(columns);
    }

    const beams: marietta.webcad.item.MonicaObject[] = tarFloor.layer_beam.getAllChildren();
    if (beams.length > 0) {
      rooms = rooms.concat(beams);
    }

    let wallList: marietta.webcad.item.WallObject[] = tarFloor.layer_wall.getAllChildren() as marietta.webcad.item.WallObject[];

    let prevFloor: number;
    if (marietta.webcad.WebCADGlobal.webcad2Dmanager.currentFloor === 1) {
      prevFloor = marietta.webcad.WebCADGlobal.webcad2Dmanager.currentFloor + 1;
    } else if (marietta.webcad.WebCADGlobal.webcad2Dmanager.currentFloor === 2) {
      prevFloor = marietta.webcad.WebCADGlobal.webcad2Dmanager.currentFloor - 1;
    }
    const tarPrevFloor = marietta.webcad.WebCADGlobal.layerGroup.getFloorLayer(prevFloor);
    if (tarPrevFloor) {
      const prevFloorRooms: marietta.webcad.item.MonicaObject[] = tarPrevFloor.layer_room.getAllChildren() as marietta.webcad.item.MonicaObject[];
      rooms = rooms.concat(prevFloorRooms);

      const prevFloorWalls: marietta.webcad.item.WallObject[] = tarPrevFloor.layer_wall.getAllChildren() as marietta.webcad.item.WallObject[];
      wallList = wallList.concat(prevFloorWalls);
    }
    this.snapElements = [];
    let a; let b; let c;
    for (let j = 0; j < rooms.length; j += 1) {
      for (let i = 0; i < rooms[j].itemInfo.dotList.length; i += 1) {
        if (i < rooms[j].itemInfo.dotList.length - 1) { 
          marietta.webcad.WebCADGlobal.snapManager.addLineSegmentSnap(marietta.webcad.WebCADGlobal.snapManager.snapElements, (rooms[j].itemInfo.x + rooms[j].itemInfo.dotList[i].x), (rooms[j].itemInfo.y + rooms[j].itemInfo.dotList[i].y),
            (rooms[j].itemInfo.x + rooms[j].itemInfo.dotList[i + 1].x), (rooms[j].itemInfo.y + rooms[j].itemInfo.dotList[i + 1].y), SnapManager.SNAP_RADIUS, 1, (`${rooms[j].itemInfo.id}_${rooms[j].itemInfo.index}_line_${j}`));
        } else {
          marietta.webcad.WebCADGlobal.snapManager.addLineSegmentSnap(marietta.webcad.WebCADGlobal.snapManager.snapElements, rooms[j].itemInfo.x + rooms[j].itemInfo.dotList[i].x, rooms[j].itemInfo.y + rooms[j].itemInfo.dotList[i].y,
            rooms[j].itemInfo.x + rooms[j].itemInfo.dotList[0].x, rooms[j].itemInfo.y + rooms[j].itemInfo.dotList[0].y, SnapManager.SNAP_RADIUS, 1, (`${rooms[j].itemInfo.id}_${rooms[j].itemInfo.index}_line_${j}`)); 
        }

        marietta.webcad.WebCADGlobal.snapManager.addPointSnap(marietta.webcad.WebCADGlobal.snapManager.snapElements, rooms[j].itemInfo.x + rooms[j].itemInfo.dotList[i].x, rooms[j].itemInfo.y + rooms[j].itemInfo.dotList[i].y, SnapManager.SNAP_RADIUS, 10, (`${rooms[j].itemInfo.id}_${rooms[j].itemInfo.index}_dot_${j}`));

        ({ a, b, c } = marietta.webcad.WebCADGlobal.snapManager.horizontalLine(rooms[j].itemInfo.y + rooms[j].itemInfo.dotList[i].y));
        lineSnap.add(marietta.webcad.WebCADGlobal.snapManager.snapElements, a, b, c, SnapManager.SNAP_RADIUS, 1, null, true);
        ({ a, b, c } = marietta.webcad.WebCADGlobal.snapManager.verticalLine(rooms[j].itemInfo.x + rooms[j].itemInfo.dotList[i].x));
        lineSnap.add(marietta.webcad.WebCADGlobal.snapManager.snapElements, a, b, c, SnapManager.SNAP_RADIUS, 1, null, true);
      }
    }

    for (let i: number = 0; i < wallList.length; i += 1) {
      const { dotList } = wallList[i].itemInfo;
      marietta.webcad.WebCADGlobal.snapManager.addLineSegmentSnap(marietta.webcad.WebCADGlobal.snapManager.snapElements, dotList[0].x, dotList[0].y, dotList[1].x, dotList[1].y, SnapManager.SNAP_RADIUS, 1, (`${wallList[i].itemInfo.id}_${wallList[i].itemInfo.index}_line_${i}`));

      marietta.webcad.WebCADGlobal.snapManager.addPointSnap(marietta.webcad.WebCADGlobal.snapManager.snapElements, dotList[0].x, dotList[0].y, SnapManager.SNAP_RADIUS, 10, (`${wallList[i].itemInfo.id}_${wallList[i].itemInfo.index}_dot_${0}`));
      ({ a, b, c } = marietta.webcad.WebCADGlobal.snapManager.horizontalLine(dotList[0].y));
      lineSnap.add(marietta.webcad.WebCADGlobal.snapManager.snapElements, a, b, c, SnapManager.SNAP_RADIUS, 1, null, false);
      ({ a, b, c } = marietta.webcad.WebCADGlobal.snapManager.verticalLine(dotList[0].x));
      lineSnap.add(marietta.webcad.WebCADGlobal.snapManager.snapElements, a, b, c, SnapManager.SNAP_RADIUS, 1, null, false);
      marietta.webcad.WebCADGlobal.snapManager.addPointSnap(marietta.webcad.WebCADGlobal.snapManager.snapElements, dotList[1].x, dotList[1].y, SnapManager.SNAP_RADIUS, 10, (`${wallList[i].itemInfo.id}_${wallList[i].itemInfo.index}_dot_${1}`));
      ({ a, b, c } = marietta.webcad.WebCADGlobal.snapManager.horizontalLine(dotList[1].y));
      lineSnap.add(marietta.webcad.WebCADGlobal.snapManager.snapElements, a, b, c, SnapManager.SNAP_RADIUS, 1, null, false);
      ({ a, b, c } = marietta.webcad.WebCADGlobal.snapManager.verticalLine(dotList[1].x));
      lineSnap.add(marietta.webcad.WebCADGlobal.snapManager.snapElements, a, b, c, SnapManager.SNAP_RADIUS, 1, null, false);
    }

    // 重複するポイントを削除する
    const noDupeObj = [];
    const elem = marietta.webcad.WebCADGlobal.snapManager.snapElements;
    for (let p = 0; p < elem.length; p += 1) {
      if (elem[p].type === 'point') {
        const pnt = elem[p] as marietta.webcad.manager.parts.PointSnap;
        noDupeObj[`${pnt.type}|${pnt.x}|${pnt.y}|${pnt.radius}`] = pnt;
      }

      if (elem[p].type === 'line') {
        const line = elem[p] as marietta.webcad.manager.parts.LineSnap;
        noDupeObj[`${line.type}|${line.a}|${line.b}|${line.c}|${line.radius}`] = line;
      }

      if (elem[p].type === 'line-segment') {
        const lineSeg = elem[p] as marietta.webcad.manager.parts.LineSegmentSnap;
        noDupeObj[`${lineSeg.type}|${lineSeg.x1}|${lineSeg.y1}|${lineSeg.x2}|${lineSeg.y2}|${lineSeg.radius}`] = lineSeg;
      }
    }

    const nonDuplicatedArray = [];
    Object.keys(noDupeObj).forEach((item) => {
      nonDuplicatedArray.push(noDupeObj[item]);
    });
    marietta.webcad.WebCADGlobal.snapManager.snapElements = nonDuplicatedArray;
  }

  /**
   * 最寄りのスナップを見つける
   * @param snapElements
   * @param x
   * @param y
   * @param snapMask
   * @returns
   */
  nearestSnap = (snapElements: SnapElemnt[], x: number, y: number) => {
    const filter = {
      point: true,
      line: true,
      'line-segment': true,
      grid: false,
    };

    const snapEl = snapElements
      .filter((el: SnapElemnt) => filter[el.type] && el.isNear(x, y, (el.radius * (1 / marietta.webcad.WebCADGlobal.scaleSize))))
      .map((snap: SnapElemnt) => ({ snap, point: snap.nearestPoint(x, y) }))
      .filter(({ snap: { radius }, point: { distance } }) => distance < radius * (1 / marietta.webcad.WebCADGlobal.scaleSize))
      .sort(
        // eslint-disable-next-line no-nested-ternary
        ({ snap: { priority: p1 }, point: { distance: d1 } }, { snap: { priority: p2 }, point: { distance: d2 } }) => (p1 === p2 ? (d1 < d2 ? -1 : 1) : (p1 > p2 ? -1 : 1)),
      );
    if (snapEl.length > 0) {
      if (snapEl[0].snap.type === 'line') {
        const line = snapEl[0].snap as marietta.webcad.manager.parts.LineSnap;
        if (SnapManager.isHorizontalLine(line)) marietta.webcad.WebCADGlobal.snapManager.showHorizontalSnapLine(snapEl[0].point);
        else if (SnapManager.isVerticalLine(line)) marietta.webcad.WebCADGlobal.snapManager.showVerticalSnapLine(snapEl[0].point);
      } else if (snapEl[0].snap.type === 'point') {
        marietta.webcad.WebCADGlobal.snapManager.showCenterSnapLine(snapEl[0].point);
      } else if (snapEl[0].snap.type === 'line-segment') {
        marietta.webcad.WebCADGlobal.snapManager.showSnapLineSegment(snapEl[0].snap as LineSegmentSnap);
      }
    }
    return snapEl.length > 0 ? snapEl[0] : null;
  }

  /** 水平線（y = c）か */
  private static isHorizontalLine(line: LineSnap): boolean {
    return line.a === 0 && line.b === 1;
  }

  /** 垂直線（x = c）か */
  private static isVerticalLine(line: LineSnap): boolean {
    return line.a === 1 && line.b === 0;
  }

  /**
   * 吸着要素が拘束する軸を返す。水平線はYだけ、垂直線はXだけを拘束する。
   * 拘束されない軸は呼び出し側でグリッドに吸着させる。
   */
  public static getConstrainedAxes(element: SnapElemnt): { x: boolean, y: boolean } {
    if (element.type === 'line') {
      const line = element as LineSnap;
      if (SnapManager.isHorizontalLine(line)) return { x: false, y: true };
      if (SnapManager.isVerticalLine(line)) return { x: true, y: false };
    }
    return { x: true, y: true };
  }

  /**
   * スナップ リストにポイント スナップを追加
   * @param snapElements
   * @param x
   * @param y
   * @param radius
   * @param priority
   * @param related
   * @returns
   */
  addPointSnap = (snapElements: SnapElemnt[], x: number, y: number, radius: number, priority: number, related: string, itemType: string = 'point') => {
    snapElements.push(new marietta.webcad.manager.parts.PointSnap(x, y, radius, priority, related, itemType));
  }

  /**
   * スナップリストにラインスナップを追加
   * @param snapElements
   * @param a
   * @param b
   * @param c
   * @param radius
   * @param priority
   * @param related
   * @returns
   */
  addLineSnap(snapElements: SnapElemnt[], a: number, b: number, c: number, radius: number, priority: number, related: string, addCrossPoint: boolean = false, addCrossPointForSegment: boolean = false) {
    const alreadyPresent = snapElements.some((lineSnap: LineSnap) => lineSnap.type === 'line'
      && a === lineSnap.a
      && b === lineSnap.b
      && c === lineSnap.c
      && radius === lineSnap.radius);
    if (alreadyPresent) return;
    if (addCrossPoint || addCrossPointForSegment) {
      snapElements.forEach((s1: LineSnap&LineSegmentSnap) => {
        if (s1.type === 'line') {
          const inter = marietta.webcad.WebCADGlobal.snapManager.twoLinesIntersection(s1.a, s1.b, s1.c, a, b, c);
          if (inter) this.addPointSnap(snapElements, inter.x, inter.y, SnapManager.CROSS_POINT_SNAP_RADIUS, 40, null);
        }
        if (addCrossPointForSegment && s1.type === 'line-segment') {
          let line = (a === 0) ? { p1: new Point(0, -1 * c), p2: new Point(marietta.webcad.WebCADGlobal.LAYER_SIZE_W, -1 * c) } : { p1: new Point(-1 * c, 0), p2: new Point(-1 * c, marietta.webcad.WebCADGlobal.LAYER_SIZE_H) };
          let seg = { p1: new Point(s1.x1, s1.y1), p2: new Point(s1.x2, s1.y2) };
          let cross = marietta.webcad.util.WebCADUtil.IntersectionEdge(line.p1, line.p2, seg.p1, seg.p2);
          if (cross) this.addPointSnap(snapElements, cross.x, cross.y, SnapManager.CROSS_POINT_SNAP_RADIUS, 40, null);
        }
      });
    }
    snapElements.push(new marietta.webcad.manager.parts.LineSnap(a, b, c, radius, priority, related));
  }

  /**
   * スナップ リストに線分スナップを追加
   * @param snapElements
   * @param x1
   * @param y1
   * @param x2
   * @param y2
   * @param radius
   * @param priority
   * @param related
   * @returns
   */
  addLineSegmentSnap = (snapElements: SnapElemnt[], x1: number, y1: number, x2: number, y2: number, radius: number, priority: number, related: string, itemType: string = 'line-segment') => {
    snapElements.push(new marietta.webcad.manager.parts.LineSegmentSnap(x1, y1, x2, y2, radius, priority, related, itemType));
  }

  /**
   * 入力が進んだ水平・垂直ガイドの優先度を下げる
   */
  lowerGuidePriority = (snapElements: SnapElemnt[] = []) => {
    snapElements.forEach((elem: SnapElemnt) => {
      if (elem.priority === SnapManager.CURRENT_GUIDE_PRIORITY) {
        elem.priority = SnapManager.PAST_GUIDE_PRIORITY;
      }
    });
  }

  /**
   * 線から最も近い点を見つける
   * @param a
   * @param b
   * @param c
   * @param x
   * @param y
   * @returns
   */
  closestPointFromLine = (a: number, b: number, c: number, x: number, y: number) => {
    const denom = a * a + b * b;
    return {
      x: (b * (b * x - a * y) - a * c) / denom,
      y: ((a * -b * x + a * y) - b * c) / denom,
    };
  }

  /**
   * ラインからポイントまでの距離を見つける
   * @param a
   * @param b
   * @param c
   * @param x
   * @param y
   * @returns
   */
  distancePointFromLine = (a: number, b: number, c: number, x: number, y: number) => Math.abs(a * x + b * y + c) / Math.sqrt(a * a + b * b)

  /**
   * 2本の線の交点を見つける
   * @param a
   * @param b
   * @param c
   * @param j
   * @param k
   * @param l
   * @returns
   */
  twoLinesIntersection = (a: number, b: number, c: number, j: number, k: number, l: number) => {
    const angularCoefficientsDiff = (b * j - a * k);
    if (angularCoefficientsDiff === 0) return undefined; // no intersection
    const y = (a * l - c * j) / angularCoefficientsDiff;
    const x = (c * k - b * l) / angularCoefficientsDiff;
    return { x, y };
  }

  pointsDistance = (x0: number, y0: number, x1: number, y1: number) => {
    const diffX = x0 - x1;
    const diffY = y0 - y1;

    return Math.sqrt((diffX * diffX) + (diffY * diffY));
  }

  /**
   * 線分から最も近い点を見つける
   * @param x1
   * @param y1
   * @param x2
   * @param y2
   * @param xp
   * @param yp
   * @returns
   */
  closestPointFromLineSegment = (x1: number, y1: number, x2: number, y2: number, xp: number, yp: number) => {
    if (x1 === x2) return { x: x1, y: yp };
    if (y1 === y2) return { x: xp, y: y1 };

    const m = (y2 - y1) / (x2 - x1);
    const q = y1 - m * x1;

    const mi = -1 / m;
    const qi = yp - mi * xp;

    const x = (qi - q) / (m - mi);
    const y = (m * x + q);

    return { x, y };
  }

  /**
   *
   * @param x1
   * @param y1
   * @param x2
   * @param y2
   * @param xp
   * @param yp
   * @returns
   */
  distancePointFromLineSegment = (x1: number, y1: number, x2: number, y2: number, xp: number, yp: number) => {
    const A = xp - x1;
    const B = yp - y1;
    const C = x2 - x1;
    const D = y2 - y1;

    const dot = A * C + B * D;
    const lensq = C * C + D * D;
    let param = -1;
    if (lensq !== 0) { // in case of 0 length line
      param = dot / lensq;
    }

    let xx; let
      yy;

    if (param < 0) {
      xx = x1;
      yy = y1;
    } else if (param > 1) {
      xx = x2;
      yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }

    const dx = xp - xx;
    const dy = yp - yy;
    return Math.sqrt(dx * dx + dy * dy);
  }

  // 水平線の基準
  horizontalLine = (y: number) => ({ a: 0, b: 1, c: -y });

  // 縦線の参考
  verticalLine = (x: number) => ({ a: 1, b: 0, c: -x });
}
